import React, { useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { Link } from 'react-router-dom';
import { NavigateToWindowWithNativeEvents } from '@components/_common/link/link';
import { Experiment, When } from '@carvana/core-experiment';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { getUserState } from '@carvana/core-auth';
import { CUSTOMER_REGISTRATION_EXPERIMENT_NAME } from '../../types';
import {
  GarageCardWrapper,
  VehicleDetails,
  ImageContainer,
  Image,
  VehicleInfo,
  Make,
  Model,
  Price,
  PaymentInfoWrapper,
  PaymentDetails,
  Label,
  PDValue,
  PaymentSchedule,
  WarningHeader,
  RedirectLinkStyled,
  VehicleDetailsRight,
  VehicleDetailsLeft,
  ExCancelIconWrapper,
} from './GarageCard.styles';
import {
  PurchaseState,
  LoanStatusType,
  NextPaymentType,
  ReferenceTypes,
  APPRAISED_STATE,
  DateMasks,
  OneTimePaymentType,
  AnalyticEvents,
} from '../../constants';
import MiniCalendarIcon from '../../assets/MiniCalendarIcon';
import WarningIcon from '../../assets/WarningIcon';

import {
  defaultImageHandler,
  formatDate,
  formatCurrency,
  getVerificationString,
} from '../../services/UtilitiesService';
import SuccessIcon from '../../assets/SuccessIcon';
import ExCancelIcon from '../../assets/ExCancelIcon';
import CancelPreOrderModal from '../_common/cancel-pre-order/CancelPreOrder';
import { deletePreOrder } from '../../store/GaragePageStore';
import CancelPrePurchaseModal from '../_common/cancel-pre-purchase/CancelPrePurchase';
import { Analytics } from '../../services/analytics-service';

/* Vehicle Details Helper */

export const VehicleDetailsWrapper = ({
  vehicleDetails,
  canBeCanceled = false,
  cardType = undefined,
  children,
}: any) => {
  const { imageUrl, year, make, model, trim, id: vehicleId } = vehicleDetails;

  /* Pre Order */

  const cardVehicleDetails = {
    vehicleId,
    vehicleState: cardType,
  };

  const [showCancelPreOrder, setShowCancelPreOrder] = useState(false);
  const [showCancelPrePurchase, setShowCancelPrePurchase] = useState(false);

  const cancelPreOrderToggle = (trackEvtMsg: string) => {
    if (!showCancelPreOrder) {
      cardVehicleDetails.vehicleState = PurchaseState.RESERVED;
      Analytics.trackEvent(trackEvtMsg, cardVehicleDetails);
    }
    setShowCancelPreOrder(!showCancelPreOrder);
  };

  const cancelPrePurchaseToggle = (trackEvtMsg: string) => {
    if (!showCancelPrePurchase) {
      cardVehicleDetails.vehicleState = PurchaseState.IN_PROGRESS;
      Analytics.trackEvent(trackEvtMsg, cardVehicleDetails);
    }
    setShowCancelPrePurchase(!showCancelPrePurchase);
  };

  const toggleCancelModal = () => {
    // Close icon clicked on pre-ordered/pre-purchase vehicle card
    const trackEvntMsg = AnalyticEvents.garageVehicleCardCancelOrderClicked;

    if (cardType === PurchaseState.RESERVED) {
      cancelPreOrderToggle(trackEvntMsg);
    }

    if (cardType === PurchaseState.IN_PROGRESS) {
      cancelPrePurchaseToggle(trackEvntMsg);
    }

    return true;
  };

  /* Pre Order - End */

  return (
    <VehicleDetails>
      <VehicleDetailsLeft>
        <ImageContainer>
          <Image
            data-testid={String(`image-${year}-${make}-${model}`).replace(' ', '-')}
            src={imageUrl}
            alt={`${year} ${make} ${model} - ${trim}`}
            onError={defaultImageHandler}
          />
        </ImageContainer>
        <VehicleInfo>
          <Make
            data-testid={String(`title-${year}-${make}-${model}`).replace(' ', '-')}
          >{`${year} ${make} ${model}`}</Make>
          <Model data-testid={String(`model-${year}-${make}-${model}`).replace(' ', '-')}>{trim}</Model>
          {children}
        </VehicleInfo>
      </VehicleDetailsLeft>
      {canBeCanceled && (
        <VehicleDetailsRight>
          <ExCancelIconWrapper
            data-qa={String(`cancel-preorder-icon-${year}-${make}-${model}`).replace(' ', '-')}
            onClick={toggleCancelModal}
          >
            <ExCancelIcon width={16} height={16} />
          </ExCancelIconWrapper>
          <CancelPreOrderModal
            closeModal={toggleCancelModal}
            isOpen={showCancelPreOrder}
            vehicleDetails={cardVehicleDetails}
            submissionHandler={deletePreOrder}
          />
          <CancelPrePurchaseModal
            closeModal={toggleCancelModal}
            isOpen={showCancelPrePurchase}
            vehicleDetails={cardVehicleDetails}
          />
        </VehicleDetailsRight>
      )}
    </VehicleDetails>
  );
};

/* Renders Vehicle Card for Purchase in Progress or OPD state */
export const PrePurchaseCard = ({ vehicleInfo, trackEventHandler, captureImpression }: any) => {
  const { purchaseState, vehicle, cardPosition, hasOpenVerifications, openVerificationCount } = vehicleInfo;
  const { id: vehicleId } = vehicle;
  const isPurchaseInProgress = purchaseState === PurchaseState.IN_PROGRESS;
  const isInOrderPlacedState = purchaseState === PurchaseState.ORDER_PLACED;
  const customerRequestsCount = openVerificationCount;
  const canUseCancelButton = isPurchaseInProgress;

  // Redirect to purchase dashboard
  const handleRedirect = () => {
    if (isInOrderPlacedState) {
      trackEventHandler({
        vehicleId,
        vehicleState: purchaseState,
        cardPosition,
        customerRequestsCount,
      }); // Event handler callback
    } else {
      trackEventHandler({
        vehicleId,
        vehicleState: purchaseState,
        cardPosition,
      }); // Event handler callback
    }

    NavigateToWindowWithNativeEvents({
      url: `/purchase`,
      event: 'openDashboard',
      payload: {
        orderState: purchaseState,
      },
    });
  };

  const handleOnShow = () => {
    if (isInOrderPlacedState) {
      setTimeout(() => {
        captureImpression({
          vehicleId,
          vehicleState: purchaseState,
          cardPosition,
          customerRequestsCount,
        });
      }, 1000);
    } else {
      setTimeout(() => {
        captureImpression({
          vehicleId,
          vehicleState: purchaseState,
          cardPosition,
        });
      }, 1000);
    }
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && handleOnShow) handleOnShow();
    },
  });

  return (
    <GarageCardWrapper
      ref={ref}
      data-qa={String(`card-wrapper-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(' ', '-')}
    >
      {hasOpenVerifications && isInOrderPlacedState && (
        <WarningHeader
          data-qa={String(`verification-warning-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(' ', '-')}
        >
          <WarningIcon />
          <span>{getVerificationString(openVerificationCount)}</span>
        </WarningHeader>
      )}
      <VehicleDetailsWrapper
        vehicleDetails={vehicle}
        canBeCanceled={canUseCancelButton}
        cardType={PurchaseState.IN_PROGRESS}
      >
        <Price data-qa={String(`price-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(' ', '-')}>
          {formatCurrency(vehicle.price, 0)}{' '}
        </Price>
        {isPurchaseInProgress ? (
          <RedirectLinkStyled
            data-qa={String(`continue-purchase-link-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(
              ' ',
              '-'
            )}
            type="button"
            onClick={handleRedirect}
          >
            Continue Purchase
          </RedirectLinkStyled>
        ) : (
          <RedirectLinkStyled
            data-qa={String(`dashboard-link-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(' ', '-')}
            type="button"
            onClick={handleRedirect}
          >
            Go to Dashboard
          </RedirectLinkStyled>
        )}
      </VehicleDetailsWrapper>
    </GarageCardWrapper>
  );
};

/* Renders Vehicle Card for Purchase in Progress or OPD state */
export const PreOrderCard = ({ vehicleInfo, trackEventHandler, captureImpression }: any) => {
  const { orderState, vehicle, cardPosition } = vehicleInfo;
  const { id: vehicleId } = vehicle;
  const isPreOrder = orderState === PurchaseState.RESERVED;

  // Redirect to purchase dashboard
  const handleRedirect = () => {
    trackEventHandler({ vehicleId, vehicleState: orderState, cardPosition }); // Event handler callback
    NavigateToWindowWithNativeEvents({
      url: `/vehicle/${vehicleId}`,
      event: 'continue_to_vdp',
      payload: { vehicleId },
    });
  };

  const handleOnShow = () => {
    setTimeout(() => {
      captureImpression({ vehicleId, vehicleState: orderState, cardPosition });
    }, 1000);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && handleOnShow) handleOnShow();
    },
  });

  return (
    <GarageCardWrapper ref={ref}>
      <VehicleDetailsWrapper vehicleDetails={vehicle} canBeCanceled={isPreOrder} cardType={PurchaseState.RESERVED}>
        <Price data-qa={String(`image-${vehicle.year}-${vehicle.make}-${vehicle.model}`).replace(' ', '-')}>
          {formatCurrency(vehicle.price, 0)}{' '}
        </Price>
        <RedirectLinkStyled
          data-qa={String(
            `check-status-vehicle-link-${vehicleInfo.year}-${vehicleInfo.make}-${vehicleInfo.model}`
          ).replace(' ', '-')}
          type="button"
          onClick={handleRedirect}
        >
          Check Status
        </RedirectLinkStyled>
      </VehicleDetailsWrapper>
    </GarageCardWrapper>
  );
};

/* Payment Details Helper */
export const PaymentInfo = ({ amountDue, dueDate, dataQa }: any) => (
  <PaymentInfoWrapper>
    <PaymentDetails>
      <Label data-testid={`amount-due-title-${dataQa}`}>Amount Due:</Label>
      <PDValue data-testid={`amount-due-value-${dataQa}`}>{formatCurrency(amountDue)}</PDValue>
    </PaymentDetails>
    <PaymentDetails>
      <Label data-testid={`due-date-title-${dataQa}`}>Due Date: </Label>
      <PDValue data-testid={`due-date-value-${dataQa}`}>{dueDate}</PDValue>
    </PaymentDetails>
  </PaymentInfoWrapper>
);

/* Renders Vehicle Card for Vehicle already Purchased */
export const PostPurchaseCard = ({ vehicleInfo, trackEventHandler, captureImpression }: any) => {
  const loanSummaryCopy = {
    loanSummary: {
      dueDate: '',
      nextPaymentDate: '',
      amountDue: '',
      nextPaymentType: '',
      loanPaymentStatus: LoanStatusType.NO_LOAN,
    },
  };
  const vehicleInfoCopy = { ...loanSummaryCopy, ...vehicleInfo };
  const {
    vehicle: { id: vehicleId, imageUrl, make, model, trim, year },
    loanSummary: { dueDate, nextPaymentDate, amountDue, nextPaymentType, loanPaymentStatus },
    purchaseState,
    cardPosition,
  } = vehicleInfoCopy;

  const vehicleDetails = {
    imageUrl,
    year,
    make,
    model,
    trim,
  };

  const isPaymentOverdue = loanPaymentStatus === LoanStatusType.OVERDUE;
  const isLoanPaidOff = loanPaymentStatus === LoanStatusType.PAID_OFF;
  const isPaymentScheduled = nextPaymentType !== NextPaymentType.NONE;
  const paymentType =
    nextPaymentType === NextPaymentType.ONE_TIME_PAYMENT ? OneTimePaymentType.PAYMENT : OneTimePaymentType.AUTO_PAY;
  const isPaymentDataVisible = ![LoanStatusType.NO_LOAN, LoanStatusType.PAID_OFF].some(
    (status: any) => status === loanPaymentStatus
  );

  let dueDateValue;

  if (isPaymentDataVisible) {
    dueDateValue = formatDate(dueDate, DateMasks.MonthDay);
    if (isPaymentOverdue) {
      dueDateValue = 'Due Now';
    }
  }

  // Redirect to OVP - using this to prevent incorrect url being picked by event context
  const handleRedirect = () => {
    trackEventHandler({ vehicleId, vehicleState: purchaseState, cardPosition }); // Event handler callback
  };

  const redirectUrl = `/my-carvana/vehicle/${vehicleId}?referenceType=${ReferenceTypes.VEHICLE}`;

  const handleOnShow = () => {
    setTimeout(() => {
      captureImpression({
        vehicleId,
        vehicleState: purchaseState,
        cardPosition,
      });
    }, 1000);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && handleOnShow) handleOnShow();
    },
  });

  const userId = getUserState()?.user?.user_id;

  return (
    <GarageCardWrapper
      ref={ref}
      data-qa={String(`card-wrapper-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`).replace(
        ' ',
        '-'
      )}
    >
      <Experiment name={CUSTOMER_REGISTRATION_EXPERIMENT_NAME} identifier={userId}>
        <When bucket="treatment">
          <MfeReactComponent
            env={process.env.NODE_ENV}
            module="CustomerRegistrationBannerMFE"
            component="default"
            vehicleId={vehicleId}
          />
        </When>
        <When default>
          <></>
        </When>
      </Experiment>
      {isPaymentOverdue && (
        <WarningHeader
          data-testid={String(
            `payment-overdue-warning-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`
          ).replace(' ', '-')}
        >
          <WarningIcon />
          <span>Payment Overdue</span>
        </WarningHeader>
      )}
      <VehicleDetailsWrapper vehicleDetails={vehicleDetails}>
        {isPaymentDataVisible && (
          <PaymentInfo
            dataQa={String(`${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`).replace(' ', '-')}
            amountDue={amountDue}
            dueDate={dueDateValue}
          />
        )}
        <Link onClick={handleRedirect} to={redirectUrl}>
          <RedirectLinkStyled
            data-testid={String(
              `purchased-view-vehicle-link-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`
            ).replace(' ', '-')}
            type="button"
          >
            View Vehicle
          </RedirectLinkStyled>
        </Link>
      </VehicleDetailsWrapper>
      {isPaymentScheduled && (
        <PaymentSchedule
          data-testid={String(
            `payment-scheduled-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`
          ).replace(' ', '-')}
        >
          <MiniCalendarIcon /> {paymentType} scheduled for {formatDate(nextPaymentDate, DateMasks.MonthDay)}
        </PaymentSchedule>
      )}
      {isLoanPaidOff && (
        <PaymentSchedule
          data-testid={String(
            `payments-complete-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`
          ).replace(' ', '-')}
        >
          <SuccessIcon /> Congrats, your loan is paid off!
        </PaymentSchedule>
      )}
    </GarageCardWrapper>
  );
};

/* Renders Vehicle Card for Appraised Vehicles */
export const AppraisedVehicleCard = ({ vehicleInfo, trackEventHandler, captureImpression }: any) => {
  const {
    vehicle: { imageUrl, make, model, trim, year },
    appraisalId,
    cardPosition,
  } = vehicleInfo;

  const vehicleDetails = {
    imageUrl,
    year,
    make,
    model,
    trim,
  };

  // Redirect to OVP - using this to prevent incorrect url being picked by event context
  const handleRedirect = () => {
    trackEventHandler({
      vehicleId: appraisalId,
      vehicleState: APPRAISED_STATE,
      cardPosition,
    }); // Event handler callback
  };

  const redirectUrl = `/my-carvana/vehicle/${appraisalId}?referenceType=${ReferenceTypes.APPRAISAL}`;

  const handleOnShow = () => {
    setTimeout(() => {
      captureImpression({
        vehicleId: appraisalId,
        vehicleState: APPRAISED_STATE,
        cardPosition,
      });
    }, 1000);
  };
  const { ref } = useIntersectionObserver({
    freezeOnceVisible: true,
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && handleOnShow) handleOnShow();
    },
  });

  return (
    <GarageCardWrapper ref={ref}>
      <VehicleDetailsWrapper vehicleDetails={vehicleDetails}>
        <Link onClick={handleRedirect} to={redirectUrl}>
          <RedirectLinkStyled
            data-qa={String(
              `appraised-view-vehicle-link-${vehicleDetails.year}-${vehicleDetails.make}-${vehicleDetails.model}`
            ).replace(' ', '-')}
            type="button"
          >
            View Vehicle
          </RedirectLinkStyled>
        </Link>
      </VehicleDetailsWrapper>
    </GarageCardWrapper>
  );
};
